import { getCategories } from './../services/services'
import { type Getter, type Setter } from 'jotai'
import {
  categoriesPagedListAtom,
  categoriesQueryAtom,
  defaultPagedQuery,
} from '../state/state'
import { type CategoryPagedListQuery } from '../../../types/beFiltersApi'
import { errorUpdater, pageState, statusUpdater } from '../../../state/page'
import { logAppError } from '../../../helpers/logger'

export const searchCategories = async (
  get: Getter,
  set: Setter,
): Promise<void> => {
  set(pageState, statusUpdater('fetching'))
  try {
    const query = get(categoriesQueryAtom)
    const result = await getCategories(query)
    set(categoriesPagedListAtom, result)
    set(pageState, statusUpdater('ready'))
  } catch (e) {
    console.error('### error', e)
    logAppError({
      Component: 'NewCategoriesListPage',
      Method: 'searchCategories',
      appError: e instanceof Error ? e : undefined,
    })
    set(pageState, errorUpdater)
    throw e
  }
}

export const changeCategoriesQueryAndSearch = async (
  get: Getter,
  set: Setter,
  part: Partial<CategoryPagedListQuery>,
): Promise<void> => {
  set(categoriesQueryAtom, (prev) => {
    return { ...prev, ...part }
  })
  try {
    await searchCategories(get, set)
  } catch {
    // do nothing, searchCategories already logs the error
  }
}

export const resetCategoriesQueryAndSearch = async (
  get: Getter,
  set: Setter,
): Promise<void> => {
  set(categoriesQueryAtom, defaultPagedQuery)
  try {
    await searchCategories(get, set)
  } catch {
    // do nothing, searchCategories already logs the error
  }
}

export const changeCategoriesPage = async (
  get: Getter,
  set: Setter,
  page: number,
): Promise<void> => {
  await changeCategoriesQueryAndSearch(get, set, { pageNumber: page })
}

export const changeCategoriesPageSize = async (
  get: Getter,
  set: Setter,
  size: number,
): Promise<void> => {
  await changeCategoriesQueryAndSearch(get, set, {
    pageNumber: 1,
    pageSize: size,
  })
}

export const changeCategoriesFieldOrdering = async (
  get: Getter,
  set: Setter,
  fieldName: string,
): Promise<void> => {
  const oldQuery = get(categoriesQueryAtom)
  const newQuery = {
    orderBy: fieldName,
    orderDescending:
      oldQuery.orderBy === fieldName
        ? !(oldQuery.orderDescending ?? false)
        : false,
    pageNumber: 1,
  }
  await changeCategoriesQueryAndSearch(get, set, newQuery)
}
